// Notification Types für ParentPath

export type NotificationType =
  // Partner Events
  | 'partner_connected'
  | 'partner_removed'
  // Namen-Swipen
  | 'name_match'
  | 'partner_favorited_name'
  // Checklisten
  | 'checklist_item_completed'
  | 'checklist_item_completed_batch'
  | 'checklist_note_added'
  | 'checklist_created'
  // Kalender
  | 'appointment_created'
  | 'appointment_created_batch'
  | 'appointment_reminder'
  | 'appointment_updated'
  // Schwangerschaft
  | 'new_ssw'
  | 'trimester_change'
  // Wehenzähler
  | 'contraction_tracking_started'
  | 'contraction_hospital_rule_met'
  | 'contraction_emergency_event'
  | 'contraction_stats_update'
  // System
  | 'welcome'
  | 'premium_activated'
  // Admin
  | 'admin_broadcast';

export interface Notification {
  id: string;
  user_id: string;
  pregnancy_id: string | null;
  type: NotificationType;
  title: string;
  message: string;
  related_entity_type: string | null;
  related_entity_id: string | null;
  metadata: Record<string, unknown>;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  // Navigationsziele
  target_url: string | null;
  target_page: string | null;
}

export interface NotificationSettings {
  id: string;
  user_id: string;

  // Partner Events
  notify_partner_connected: boolean;
  notify_partner_removed: boolean;

  // Namen-Swipen
  notify_name_match: boolean;
  notify_partner_favorited_name: boolean;

  // Checklisten
  notify_checklist_item_completed: boolean;
  notify_checklist_note_added: boolean;
  notify_checklist_created: boolean;

  // Kalender
  notify_appointment_created: boolean;
  notify_appointment_reminder: boolean;
  notify_appointment_updated: boolean;

  // Erinnerungs-Zeitpunkte
  reminder_1_hour: boolean;
  reminder_1_day: boolean;
  reminder_1_week: boolean;

  // Schwangerschaft
  notify_new_ssw: boolean;
  notify_trimester_change: boolean;

  // Wehenzähler (globale Einstellung - Details im Tool selbst)
  notify_contraction_timer: boolean;
  // Wehenzähler (detaillierte Einstellungen)
  notify_contraction_tracking_started: boolean;
  notify_contraction_stats_update: boolean;
  notify_contraction_emergency_event: boolean;

  // Schlafplan
  notify_sleep_plan: boolean;
  notify_sleep_plan_early_warning: boolean;
  notify_sleep_plan_window_opens: boolean;
  notify_sleep_plan_window_closes: boolean;
  notify_sleep_plan_overtired: boolean;

  // Medikamenten-Plan
  notify_medication_reminder: boolean;

  // System
  notify_welcome: boolean;
  notify_premium_activated: boolean;

  // Push-Benachrichtigungen
  push_enabled: boolean;

  created_at: string;
  updated_at: string;
}

// Gruppierte Notifications für die UI
export interface GroupedNotifications {
  today: Notification[];
  yesterday: Notification[];
  thisWeek: Notification[];
  older: Notification[];
}

// Icon-Mapping für Notification-Typen
export const notificationIcons: Record<NotificationType, string> = {
  partner_connected: 'users',
  partner_removed: 'user-minus',
  name_match: 'heart',
  partner_favorited_name: 'star',
  checklist_item_completed: 'check-square',
  checklist_item_completed_batch: 'check-square',
  checklist_note_added: 'message-square',
  checklist_created: 'list',
  appointment_created: 'calendar-plus',
  appointment_created_batch: 'calendar-plus',
  appointment_reminder: 'bell',
  appointment_updated: 'calendar',
  new_ssw: 'baby',
  trimester_change: 'milestone',
  contraction_tracking_started: 'timer',
  contraction_hospital_rule_met: 'alert-triangle',
  contraction_emergency_event: 'siren',
  contraction_stats_update: 'bar-chart-2',
  welcome: 'sparkles',
  premium_activated: 'crown',
  admin_broadcast: 'megaphone',
};

// Farb-Mapping für Notification-Typen
export const notificationColors: Record<NotificationType, string> = {
  partner_connected: 'text-green-600 bg-green-100',
  partner_removed: 'text-red-600 bg-red-100',
  name_match: 'text-pink-600 bg-pink-100',
  partner_favorited_name: 'text-yellow-600 bg-yellow-100',
  checklist_item_completed: 'text-primary-600 bg-primary-100',
  checklist_item_completed_batch: 'text-primary-600 bg-primary-100',
  checklist_note_added: 'text-blue-600 bg-blue-100',
  checklist_created: 'text-primary-600 bg-primary-100',
  appointment_created: 'text-purple-600 bg-purple-100',
  appointment_created_batch: 'text-purple-600 bg-purple-100',
  appointment_reminder: 'text-orange-600 bg-orange-100',
  appointment_updated: 'text-purple-600 bg-purple-100',
  new_ssw: 'text-primary-600 bg-primary-100',
  trimester_change: 'text-primary-600 bg-primary-100',
  contraction_tracking_started: 'text-blue-600 bg-blue-100',
  contraction_hospital_rule_met: 'text-red-600 bg-red-100',
  contraction_emergency_event: 'text-red-600 bg-red-100',
  contraction_stats_update: 'text-purple-600 bg-purple-100',
  welcome: 'text-primary-600 bg-primary-100',
  premium_activated: 'text-yellow-600 bg-yellow-100',
  admin_broadcast: 'text-blue-600 bg-blue-100',
};

// Interne Seiten-Mapping
export const internalPageRoutes: Record<string, string> = {
  dashboard: '/',
  ssw: '/ssw',
  tools: '/tools',
  chat: '/chat',
  profile: '/profile',
  calendar: '/calendar',
  partner: '/partner',
  help: '/help',
};

// Navigationsziel für eine Notification ermitteln
export function getNotificationTarget(notification: Notification): string | null {
  // Admin-Broadcast: Custom URL oder interne Seite
  if (notification.type === 'admin_broadcast') {
    if (notification.target_url) {
      return notification.target_url;
    }
    if (notification.target_page) {
      return internalPageRoutes[notification.target_page] || '/';
    }
    return null;
  }

  // Batch-Typen: Zur Übersichtsseite navigieren
  if (notification.type === 'appointment_created_batch') {
    return '/calendar';
  }
  if (notification.type === 'checklist_item_completed_batch') {
    return '/tools/checklists';
  }

  // Checklisten: Zur spezifischen Checkliste navigieren
  if (
    notification.type === 'checklist_item_completed' ||
    notification.type === 'checklist_note_added' ||
    notification.type === 'checklist_created'
  ) {
    const checklistId = notification.metadata?.checklist_id as string | undefined;
    if (checklistId) {
      return `/tools/checklists/${checklistId}`;
    }
    return '/tools/checklists';
  }

  // Standard-Mappings für andere Typen
  const defaultRoutes: Record<NotificationType, string> = {
    partner_connected: '/partner',
    partner_removed: '/partner',
    name_match: '/tools/names/matches',
    partner_favorited_name: '/tools/names/matches',
    checklist_item_completed: '/tools/checklists',
    checklist_item_completed_batch: '/tools/checklists',
    checklist_note_added: '/tools/checklists',
    checklist_created: '/tools/checklists',
    appointment_created: '/calendar',
    appointment_created_batch: '/calendar',
    appointment_reminder: '/calendar',
    appointment_updated: '/calendar',
    new_ssw: '/ssw',
    trimester_change: '/ssw',
    contraction_tracking_started: '/tools/contraction-timer',
    contraction_hospital_rule_met: '/tools/contraction-timer',
    contraction_emergency_event: '/tools/contraction-timer',
    contraction_stats_update: '/tools/contraction-timer',
    welcome: '/',
    premium_activated: '/subscription',
    admin_broadcast: '/',
  };

  return defaultRoutes[notification.type] || null;
}

// Settings-Labels für die Einstellungsseite
export interface NotificationSettingGroup {
  title: string;
  description?: string;
  settings: {
    key: keyof Omit<NotificationSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'>;
    label: string;
    description?: string;
  }[];
}

export const notificationSettingGroups: NotificationSettingGroup[] = [
  {
    title: 'Partner',
    description: 'Benachrichtigungen zu deinem Partner',
    settings: [
      {
        key: 'notify_partner_connected',
        label: 'Partner verbunden',
        description: 'Wenn dein Partner die Einladung annimmt',
      },
      {
        key: 'notify_partner_removed',
        label: 'Partner entfernt',
        description: 'Wenn die Partnerverbindung getrennt wird',
      },
    ],
  },
  {
    title: 'Namen-Swipen',
    description: 'Benachrichtigungen zum Namen-Swipen',
    settings: [
      {
        key: 'notify_name_match',
        label: 'Neuer Match',
        description: 'Wenn ihr beide einen Namen mögt',
      },
      {
        key: 'notify_partner_favorited_name',
        label: 'Partner hat favorisiert',
        description: 'Wenn dein Partner einen Namen favorisiert',
      },
    ],
  },
  {
    title: 'Checklisten',
    description: 'Benachrichtigungen zu Checklisten',
    settings: [
      {
        key: 'notify_checklist_item_completed',
        label: 'Item abgehakt',
        description: 'Wenn dein Partner ein Item erledigt',
      },
      {
        key: 'notify_checklist_note_added',
        label: 'Notiz hinzugefügt',
        description: 'Wenn dein Partner eine Notiz hinzufügt',
      },
      {
        key: 'notify_checklist_created',
        label: 'Neue Checkliste',
        description: 'Wenn dein Partner eine neue Liste erstellt',
      },
    ],
  },
  {
    title: 'Kalender',
    description: 'Benachrichtigungen zu Terminen',
    settings: [
      {
        key: 'notify_appointment_created',
        label: 'Neuer Termin',
        description: 'Wenn dein Partner einen Termin hinzufügt',
      },
      {
        key: 'notify_appointment_reminder',
        label: 'Terminerinnerungen',
        description: 'Erinnerungen vor anstehenden Terminen',
      },
      {
        key: 'notify_appointment_updated',
        label: 'Termin geändert',
        description: 'Wenn dein Partner einen Termin ändert',
      },
    ],
  },
  {
    title: 'Erinnerungs-Zeitpunkte',
    description: 'Wann möchtest du an Termine erinnert werden?',
    settings: [
      {
        key: 'reminder_1_hour',
        label: '1 Stunde vorher',
      },
      {
        key: 'reminder_1_day',
        label: '1 Tag vorher',
      },
      {
        key: 'reminder_1_week',
        label: '1 Woche vorher',
      },
    ],
  },
  {
    title: 'Schwangerschaft',
    description: 'Benachrichtigungen zu deiner Schwangerschaft',
    settings: [
      {
        key: 'notify_new_ssw',
        label: 'Neue SSW erreicht',
        description: 'Wöchentliche Benachrichtigung zur neuen Schwangerschaftswoche',
      },
      {
        key: 'notify_trimester_change',
        label: 'Trimester-Wechsel',
        description: 'Wenn du ein neues Trimester erreichst',
      },
    ],
  },
  {
    title: 'Wehenzähler',
    description: 'Benachrichtigungen vom Wehenzähler deines Partners',
    settings: [
      {
        key: 'notify_contraction_tracking_started',
        label: 'Wehenzähler gestartet',
        description: 'Wenn dein Partner das Wehen-Tracking startet',
      },
      {
        key: 'notify_contraction_stats_update',
        label: 'Zusammenfassungen',
        description: 'Regelmäßige Statistik-Updates (30/60 Min.)',
      },
      {
        key: 'notify_contraction_emergency_event',
        label: 'Notfälle',
        description: 'Blasensprung, Blutung und andere Notfall-Events',
      },
    ],
  },
  {
    title: 'Schlafplan',
    description: 'Benachrichtigungen vom Schlafplan',
    settings: [
      {
        key: 'notify_sleep_plan',
        label: 'Schlafplan-Benachrichtigungen',
        description: 'Alle Schlafplan-Benachrichtigungen aktivieren/deaktivieren',
      },
      {
        key: 'notify_sleep_plan_early_warning',
        label: 'Frühwarnung',
        description: 'Wenn das Baby bald müde wird (70% der Wachphase)',
      },
      {
        key: 'notify_sleep_plan_window_opens',
        label: 'Schlaffenster öffnet sich',
        description: 'Guter Zeitpunkt zum Hinlegen',
      },
      {
        key: 'notify_sleep_plan_window_closes',
        label: 'Schlaffenster schließt sich',
        description: 'Das Baby sollte jetzt schlafen',
      },
      {
        key: 'notify_sleep_plan_overtired',
        label: 'Übermüdungs-Warnung',
        description: 'Das Baby ist möglicherweise übermüdet',
      },
    ],
  },
  {
    title: 'Medikamenten-Plan',
    description: 'Erinnerungen an Medikamente',
    settings: [
      {
        key: 'notify_medication_reminder',
        label: 'Medikamenten-Erinnerungen',
        description: 'Benachrichtigungen wenn ein Medikament fällig ist',
      },
    ],
  },
  {
    title: 'System',
    description: 'Allgemeine Benachrichtigungen',
    settings: [
      {
        key: 'notify_welcome',
        label: 'Willkommen',
        description: 'Willkommensnachricht bei der Registrierung',
      },
      {
        key: 'notify_premium_activated',
        label: 'Premium aktiviert',
        description: 'Wenn dein Premium-Abo aktiviert wird',
      },
      {
        key: 'push_enabled',
        label: 'Push-Benachrichtigungen',
        description: 'Benachrichtigungen auf dem Sperrbildschirm erhalten',
      },
    ],
  },
];
